import React, { useEffect, useState } from "react";
import {
  Button,
  Fade,
  MobileStepper,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import { useDispatch, useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { getUserExistingZoneBooking } from "../../utils/axios-requests";
import {
  checkModeVehicle,
  checkModeVisitor,
  disableNextRoomBookingInputs,
  nextStepCalc,
  placeMode,
  prevStepCalc,
  resetSelection,
  zoneMode
} from "./functions/booking-form.functions";
import { clearBookingFilters, clearFloors, setInputs } from "./slices/booking.slice";
import StepContent from "./step-content.component";
import {
  conferenceInviteVisitorStepBlocked,
  isVisitorRegistrationStepComplete,
  visitorRequestsPlaceOrRoom
} from "./functions/visitor-registration.functions";
import { BookingType, Frequence, NotSerializedBookingInputs } from "./typings/booking-inputs";
import { bookingRestrictionsResponse } from "./typings/schema";

/**
 * @description The booking form contains the main inputs of the booking process (state)
 * @version 0.1.1
 */

const basePath = "/booking/"; // Should not hardcode this

export const validateDateRange = (
  inputs: NotSerializedBookingInputs,
  validDates: boolean
): boolean => {
  if (
    inputs.frequence &&
    [Frequence.DAILY, Frequence.WEEKLY].includes(inputs.frequence) &&
    (inputs.interval === undefined || inputs.interval <= 0)
  )
    return true;
  // !validDates || !inputs.timeSpanBigEnough
  return !validDates || !Object.values(inputs.weekdays).includes(true) || !inputs.timeSpanBigEnough;
};

export const BookingForm: React.FC = () => {
  const { inputs } = useSelector((state: RootState) => state.booking);
  const { userInformation } = useSelector((state: RootState) => state.login);

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const theme = useTheme();
  const dispatch = useDispatch();

  const [validDates, setValidDates] = useState(false);
  const [adjustedData, setAdjustedData] = useState<any>();

  const { step }: { step: string } = useParams();
  const history = useHistory();

  useEffect(() => {
    if (!inputs.userBookedBy) dispatch(setInputs({ userBookedBy: userInformation.sub }));
  }, [dispatch, inputs.userBookedBy, userInformation.sub]);

  useEffect(() => {
    dispatch(clearBookingFilters());
  }, [dispatch, inputs.mode]);
  /*
   * Functions
   */

  const nextStep = (to?: number) =>
    history.push(basePath + nextStepCalc(Number.parseInt(step), to).toString(), {
      prev: Number.parseInt(step)
    });

  const prevStep = (to?: number) =>
    history.push(basePath + prevStepCalc(Number.parseInt(step), inputs, dispatch, to).toString(), {
      prev: Number.parseInt(step)
    });

  function getSteps() {
    switch (inputs.mode) {
      case BookingType.WORKPLACE:
        return [t("Mode"), t("Date and Time"), t("Location"), t("Workplace")];
      case BookingType.PARKINGPLACE:
        return [t("Mode"), t("Date and Time"), t("Location"), t("Place")];
      case BookingType.ELECTRICCHARGINGSTATIONPLACE:
        return [t("Mode"), t("Date and Time"), t("Location"), t("Station")];
      case BookingType.TEAM:
        return [t("Mode"), t("Date and Time"), t("Team"), t("Location"), t("Workplace")];
      case BookingType.VISITOR:
        if (!visitorRequestsPlaceOrRoom(inputs)) {
          return [
            t("Mode"),
            t("Date and Time"),
            t("_visitorBooking_registrationStep"),
            t("Confirm")
          ];
        }
        return [
          t("Mode"),
          t("Date and Time"),
          t("_visitorBooking_registrationStep"),
          t("Location"),
          t("Workplace"),
          t("Confirm")
        ];
      case BookingType.PLACEZONE:
        return [t("Mode"), t("Date"), t("Location"), t("Room"), t("Restriction")];
      case BookingType.CONFERENCEZONE:
        return [t("Mode"), t("Date and Time"), t("Invite"), t("Location"), t("Room")];
      case BookingType.VEHICLE:
        return [t("Mode"), t("Date and Time"), t("Availability"), t("_bookingTypeVehicle")];
      default:
        return [t("Mode"), t("Date and Time"), t("Location"), t("Room")];
    }
  }

  const steps = getSteps();

  /**
   * @description Checks existing bookings and takes action based on result.
   * - If there are conflicts, open conflict resolve modal
   * - If there are no conflicts, continue to the next step
   * - If there is an error getting conflicts, continue to the next step
   */

  // This whole component needs refactorings
  const checkExistingBookings = () => {
    if (inputs.mode == BookingType.PLACEZONE)
      getUserExistingZoneBooking({
        zoneBookingUsers: inputs.usersBookedFor,
        zoneBookingObject: inputs.restrictionObject.object.zoneBookingObject,
        zoneBookingObjectType: inputs.restrictionObject.object.type,
        zoneBookingObjectClient: inputs.restrictionObject.company.id,
        users: inputs.usersBookedFor!.map(el => ({
          userId: el.userId,
          isExternal: false
        })),
        startDate: inputs.bookingFrom,
        endDate: inputs.bookingTo,
        startTime: inputs.bookingStart,
        endTime: inputs.bookingEnd,
        bookingType: inputs.bookingType,
        weekdays: inputs.weekdays
      })
        .then(res => {
          if (res.data.length) {
            const data = bookingRestrictionsResponse.try(res.data);
            if (data instanceof Error) {
              enqueueSnackbar(t("Could not check your existing bookings"), { variant: "warning" });
            } else {
              const adjustedDataMap = data.map(o => ({
                ...o,
                start: o.start,
                end: o.end
              }));
              setAdjustedData(adjustedDataMap);
            }
          } else {
            nextStep();
          }
        })
        .catch(() => enqueueSnackbar(t("errorRetrievingExistingZoneBooking")));
  };

  const step1 = () => {
    // prohibit continuation when the interval field is empty while daily or weekly are selected
    return validateDateRange(inputs, validDates);
  };

  const step2 = () => {
    if (BookingType.CONFERENCEZONE === inputs.mode) {
      if (!inputs.subject) {
        return true;
      }
      if (conferenceInviteVisitorStepBlocked(inputs)) {
        return true;
      }
      return false;
    }

    if (checkModeVisitor(inputs)) {
      return !isVisitorRegistrationStepComplete(inputs);
    }

    if (checkModeVehicle(inputs)) {
      return (
        inputs.hasCheckedInFleetConflict === true ||
        !inputs.bookedVehicle ||
        inputs.usersBookedFor?.some(({ bookingInventoryId }) => !bookingInventoryId)
      );
    }

    if (inputs.mode !== BookingType.TEAM) {
      return true;
    } else if (inputs.usersBookedFor) {
      return inputs.usersBookedFor.length < 1;
    } else return true;
  };

  const step3 = () => {
    if (checkModeVehicle(inputs)) return true;
    if (checkModeVisitor(inputs)) {
      if (!visitorRequestsPlaceOrRoom(inputs)) return true;
      return inputs.selectedLocation == null;
    }

    if (inputs.mode !== BookingType.TEAM) {
      return inputs.usersBookedFor?.some(({ bookingInventoryId }) => !bookingInventoryId);
    }
    return true;
  };

  const step4 = () => {
    if (placeMode(inputs)) {
      return true;
    }
    if (zoneMode(inputs)) {
      return disableNextRoomBookingInputs(inputs, userInformation);
    }
    if (inputs.mode === BookingType.TEAM) {
      return Boolean(inputs.usersBookedFor?.some(({ bookingInventoryId }) => !bookingInventoryId));
    }
  };

  /**
   * Step 5 shows FormConfirm (or Success for desk-style place modes). `placeMode` includes VISITOR,
   * so `!placeMode` alone would wrongly leave Next enabled on visitor confirm — disable it there.
   * Team and other non-place modes already have `!placeMode === true`.
   */
  const step5 = !placeMode(inputs) || checkModeVisitor(inputs);

  const getDisabled = () => {
    switch (step) {
      case "0":
        return true;
      case "1":
        return step1();
      case "2":
        return step2();
      case "3":
        return step3();
      case "4":
        return step4();
      case "5":
        return step5;
    }
    return true;
  };

  useEffect(() => {
    const s = Number.parseInt(step);
    if (s > 0 && !inputs.mode) history.push(basePath + "0");
  }, [history, inputs.mode, step]);

  return (
    <>
      {Number(step) >= 1 && (
        <>
          <Fade in>
            <Stepper
              data-testid="booking-form-stepper"
              activeStep={Number(step)}
              sx={{
                display: { xs: "none", md: "flex" },
                padding: "24px",
                backgroundColor: theme.palette.background.paper
              }}
            >
              {steps.map(label => {
                const stepProps: { completed?: boolean } = {};
                const labelProps: { optional?: React.ReactNode } = {};

                return (
                  <Step key={label} {...stepProps}>
                    <StepLabel {...labelProps}>{label}</StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Fade>
          <Fade in>
            <MobileStepper
              sx={{ pl: 0, pr: 0, pt: 1.5, pb: 1.75 }}
              variant="dots"
              steps={getSteps().length}
              position="static"
              activeStep={Number(step)}
              nextButton={
                <>
                  {step === "4" && inputs.mode === BookingType.TEAM && getDisabled() ? (
                    <Tooltip
                      title={
                        <Typography>{t("_notAllowedContinueBeforeAllTeamUserBooked")}</Typography>
                      }
                    >
                      <span>
                        <Button
                          data-testid="next-button"
                          size="small"
                          color={"primary"}
                          disabled={getDisabled()} // true
                        >
                          {t("Next")}
                          {<KeyboardArrowRight />}
                        </Button>
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip title={<Typography>{t("Next")}</Typography>}>
                      <span>
                        <Button
                          data-testid="next-button"
                          size="small"
                          onClick={() => {
                            if (step === "4" && inputs.mode === BookingType.PLACEZONE) {
                              checkExistingBookings();
                            } else if (
                              step === "2" &&
                              inputs.mode === BookingType.VISITOR &&
                              !visitorRequestsPlaceOrRoom(inputs)
                            ) {
                              dispatch(clearFloors());
                              dispatch(
                                setInputs({
                                  selectedLocation: null,
                                  floorInventoryId: undefined,
                                  locationName: null
                                })
                              );
                              nextStep();
                            } else {
                              nextStep();
                            }
                          }}
                          color={"primary"}
                          disabled={getDisabled()}
                        >
                          {t("Next")}
                          {<KeyboardArrowRight />}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </>
              }
              backButton={
                <Tooltip title={<Typography>{t("Back")}</Typography>}>
                  <span>
                    <Button
                      data-testid="back-button"
                      disabled={step === "0" || Number(step) === getSteps().length + 1}
                      size="small"
                      color="secondary"
                      onClick={() => {
                        prevStep();
                        resetSelection(step, inputs, userInformation, dispatch);
                      }} /* disabled={activeStep === 0} */
                    >
                      <KeyboardArrowLeft />
                      {t("Back")}
                    </Button>
                  </span>
                </Tooltip>
              }
            />
          </Fade>
        </>
      )}

      <StepContent
        step={step}
        nextStep={nextStep}
        adjustedData={adjustedData}
        setValidDates={setValidDates}
        validDates={validDates}
      />
    </>
  );
};

export default BookingForm;
